import { cva } from 'class-variance-authority';

export const iconVariants = cva(
  [
    '[&_>svg]:w-sizing-sys-size-24',
    '[&_>svg]:h-sizing-sys-size-24',
    'flex-none',
    'cursor-pointer',
    'flex items-center',
    'justify-center',
    'fill-current',
  ],
  {
    variants: {
      size: {
        tiny: [
          'w-sizing-comp-tag-tiny-icon',
          'h-sizing-comp-tag-tiny-icon',
          'first-line:h-sizing-comp-button-tiny-icon',
        ],
        small: ['w-sizing-comp-tag-small-icon', 'h-sizing-comp-tag-small-icon'],
      },
    },
    defaultVariants: {
      size: 'small',
    },
  },
);

export const textAreaVariants = cva(
  ['flex', 'flex-1', 'items-center', 'justify-center'],
  {
    variants: {
      size: {
        tiny: [
          'font-comp-tag-tiny-font-family',
          'font-comp-tag-tiny-font-weight',
          'text-font-comp-tag-tiny-font-size',
          'leading-font-comp-tag-tiny-line-height',
          'px-spacing-comp-tag-tiny-horizontal',
          'py-spacing-comp-tag-tiny-vertical',
          'gap-spacing-comp-tag-tiny-gap',
          /* If there are icons, we remove the text area spacing by subtracting it from the icons margin */
          '[&>svg:first-child]:-ml-spacing-comp-tag-tiny-horizontal',
        ],
        small: [
          'font-comp-tag-small-font-family',
          'font-comp-tag-small-font-weight',
          'text-font-comp-tag-small-font-size',
          'leading-font-comp-tag-small-line-height',
          'px-spacing-comp-tag-small-horizontal',
          'py-spacing-comp-tag-small-vertical',
          'gap-spacing-comp-tag-small-gap',
          /* If there are icons, we remove the text area spacing by subtracting it from the icons margin */
          '[&>svg:first-child]:-ml-spacing-comp-tag-small-horizontal',
        ],
      },
    },
    defaultVariants: {
      size: 'small',
    },
  },
);

// Every className below must stay a literal string: Tailwind's content scanner
// statically greps source files for candidate class names and can't resolve
// template-literal interpolation (e.g. `bg-color-comp-tag-${variant}-solid-...`),
// so a computed variant of this file silently drops all color/border CSS for
// every Tag (CI build 816580). The variant×fill repetition here is inherent to
// the design-token space, not organic duplication — see sonarqube.config.ts for
// the matching sonar.cpd.exclusions entry.
export const tagVariants = cva(
  [
    'inline-flex',
    'items-center',
    'justify-center',
    'whitespace-nowrap',
    'rounded-radius-comp-tag',
    'focus-visible:outline-offset-2',
    'focus-visible:outline-color-ref-ui-focus-default',
    'disabled:pointer-events-none',
  ],
  {
    variants: {
      clickable: {
        true: 'cursor-pointer',
        false: 'cursor-default',
      },
      size: {
        tiny: [
          'py-spacing-comp-tag-tiny-vertical',
          'px-spacing-comp-tag-tiny-horizontal',
          'h-sizing-sys-size-24',
        ],
        small: [
          'py-spacing-comp-tag-small-vertical',
          'px-spacing-comp-tag-small-horizontal',
          'h-sizing-sys-size-32',
        ],
      },
      variant: {
        primary: '',
        success: '',
        danger: '',
        warning: '',
        neutral: '',
        strong: '',
        transparent: '',
      },
      fill: {
        filled: '',
        // we subtract the border width from the width to account for the border
        outline: 'border-border-comp-tag',
      },
    },
    compoundVariants: [
      // Enabled/disabled/selected tokens per variant×fill. hover:/active: tokens live in
      // the clickable-gated compoundVariants below so they never coexist in the same
      // selector as aria-disabled:/aria-selected: at higher specificity.
      {
        variant: 'primary',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-primary-solid-container-enabled',
          'text-color-comp-tag-primary-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-primary-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-primary-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-primary-solid-container-selected',
          'aria-selected:text-color-comp-tag-primary-solid-on-container-selected',
        ],
      },
      {
        variant: 'primary',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-primary-plain-container-enabled',
          'text-color-comp-tag-primary-plain-on-container-enabled',
          'border-color-comp-tag-primary-plain-border-enabled',
          'aria-disabled:bg-color-comp-tag-primary-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-primary-plain-on-container-disabled',
          'aria-disabled:border-color-comp-tag-primary-plain-border-disabled',
          'aria-selected:bg-color-comp-tag-primary-plain-container-selected',
          'aria-selected:text-color-comp-tag-primary-plain-on-container-selected',
          'aria-selected:border-color-comp-tag-primary-plain-border-selected',
        ],
      },
      {
        variant: 'success',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-success-solid-container-enabled',
          'text-color-comp-tag-success-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-success-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-success-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-success-solid-container-selected',
          'aria-selected:text-color-comp-tag-success-solid-on-container-selected',
        ],
      },
      {
        variant: 'success',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-success-plain-container-enabled',
          'text-color-comp-tag-success-plain-on-container-enabled',
          'border-color-comp-tag-success-plain-border-enabled',
          'aria-disabled:border-color-comp-tag-success-plain-border-disabled',
          'aria-disabled:bg-color-comp-tag-success-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-success-plain-on-container-disabled',
          'aria-selected:border-color-comp-tag-success-plain-border-selected',
          'aria-selected:bg-color-comp-tag-success-plain-container-selected',
          'aria-selected:text-color-comp-tag-success-plain-on-container-selected',
        ],
      },
      {
        variant: 'danger',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-danger-solid-container-enabled',
          'text-color-comp-tag-danger-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-danger-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-danger-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-danger-solid-container-selected',
          'aria-selected:text-color-comp-tag-danger-solid-on-container-selected',
        ],
      },
      {
        variant: 'danger',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-danger-plain-container-enabled',
          'text-color-comp-tag-danger-plain-on-container-enabled',
          'border-color-comp-tag-danger-plain-border-enabled',
          'aria-disabled:border-color-comp-tag-danger-plain-border-disabled',
          'aria-disabled:bg-color-comp-tag-danger-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-danger-plain-on-container-disabled',
          'aria-selected:border-color-comp-tag-danger-plain-border-selected',
          'aria-selected:bg-color-comp-tag-danger-plain-container-selected',
          'aria-selected:text-color-comp-tag-danger-plain-on-container-selected',
        ],
      },
      {
        variant: 'warning',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-warning-solid-container-enabled',
          'text-color-comp-tag-warning-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-warning-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-warning-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-warning-solid-container-selected',
          'aria-selected:text-color-comp-tag-warning-solid-on-container-selected',
        ],
      },
      {
        variant: 'warning',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-warning-plain-container-enabled',
          'text-color-comp-tag-warning-plain-on-container-enabled',
          'border-color-comp-tag-warning-plain-border-enabled',
          'aria-disabled:border-color-comp-tag-warning-plain-border-disabled',
          'aria-disabled:bg-color-comp-tag-warning-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-warning-plain-on-container-disabled',
          'aria-selected:border-color-comp-tag-warning-plain-border-selected',
          'aria-selected:bg-color-comp-tag-warning-plain-container-selected',
          'aria-selected:text-color-comp-tag-warning-plain-on-container-selected',
        ],
      },
      {
        variant: 'neutral',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-neutral-solid-container-enabled',
          'text-color-comp-tag-neutral-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-neutral-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-neutral-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-neutral-solid-container-selected',
          'aria-selected:text-color-comp-tag-neutral-solid-on-container-selected',
        ],
      },
      {
        variant: 'neutral',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-neutral-plain-container-enabled',
          'text-color-comp-tag-neutral-plain-on-container-enabled',
          'border-color-comp-tag-neutral-plain-border-enabled',
          'aria-disabled:border-color-comp-tag-neutral-plain-border-disabled',
          'aria-disabled:bg-color-comp-tag-neutral-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-neutral-plain-on-container-disabled',
          'aria-selected:border-color-comp-tag-neutral-plain-border-selected',
          'aria-selected:bg-color-comp-tag-neutral-plain-container-selected',
          'aria-selected:text-color-comp-tag-neutral-plain-on-container-selected',
        ],
      },
      {
        variant: 'strong',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-strong-solid-container-enabled',
          'text-color-comp-tag-strong-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-strong-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-strong-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-strong-solid-container-selected',
          'aria-selected:text-color-comp-tag-strong-solid-on-container-selected',
        ],
      },
      {
        variant: 'strong',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-strong-plain-container-enabled',
          'text-color-comp-tag-strong-plain-on-container-enabled',
          'border-color-comp-tag-strong-plain-border-enabled',
          'aria-disabled:border-color-comp-tag-strong-plain-border-disabled',
          'aria-disabled:bg-color-comp-tag-strong-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-strong-plain-on-container-disabled',
          'aria-selected:border-color-comp-tag-strong-plain-border-selected',
          'aria-selected:bg-color-comp-tag-strong-plain-container-selected',
          'aria-selected:text-color-comp-tag-strong-plain-on-container-selected',
        ],
      },
      {
        variant: 'transparent',
        fill: 'filled',
        className: [
          'bg-color-comp-tag-transparent-solid-container-enabled',
          'text-color-comp-tag-transparent-solid-on-container-enabled',
          'aria-disabled:bg-color-comp-tag-transparent-solid-container-disabled',
          'aria-disabled:text-color-comp-tag-transparent-solid-on-container-disabled',
          'aria-selected:bg-color-comp-tag-transparent-solid-container-selected',
          'aria-selected:text-color-comp-tag-transparent-solid-on-container-selected',
        ],
      },
      {
        variant: 'transparent',
        fill: 'outline',
        className: [
          'bg-color-comp-tag-transparent-plain-container-enabled',
          'text-color-comp-tag-transparent-plain-on-container-enabled',
          'border-color-comp-tag-transparent-plain-border-enabled',
          'aria-disabled:border-color-comp-tag-transparent-plain-border-disabled',
          'aria-disabled:bg-color-comp-tag-transparent-plain-container-disabled',
          'aria-disabled:text-color-comp-tag-transparent-plain-on-container-disabled',
          'aria-selected:border-color-comp-tag-transparent-plain-border-selected',
          'aria-selected:bg-color-comp-tag-transparent-plain-container-selected',
          'aria-selected:text-color-comp-tag-transparent-plain-on-container-selected',
        ],
      },
      // Hover/active tokens, gated on clickable: true so a non-clickable tag never
      // carries hover:/active: classes and a clickable disabled/selected tag can't
      // have them win over aria-disabled:/aria-selected: (same selector specificity,
      // see latitude/card/src/variants/card-variants.ts:40-50 for the same pattern).
      {
        clickable: true,
        variant: 'primary',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-primary-solid-container-hover',
          'hover:text-color-comp-tag-primary-solid-on-container-hover',
          'active:bg-color-comp-tag-primary-solid-container-pressed',
          'active:text-color-comp-tag-primary-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'primary',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-primary-plain-container-hover',
          'hover:text-color-comp-tag-primary-plain-on-container-hover',
          'hover:border-color-comp-tag-primary-plain-border-hover',
          'active:bg-color-comp-tag-primary-plain-container-pressed',
          'active:text-color-comp-tag-primary-plain-on-container-pressed',
          'active:border-color-comp-tag-primary-plain-border-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'success',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-success-solid-container-hover',
          'hover:text-color-comp-tag-success-solid-on-container-hover',
          'active:bg-color-comp-tag-success-solid-container-pressed',
          'active:text-color-comp-tag-success-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'success',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-success-plain-container-hover',
          'hover:text-color-comp-tag-success-plain-on-container-hover',
          'hover:border-color-comp-tag-success-plain-border-hover',
          'active:bg-color-comp-tag-success-plain-container-pressed',
          'active:text-color-comp-tag-success-plain-on-container-pressed',
          'active:border-color-comp-tag-success-plain-border-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'danger',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-danger-solid-container-hover',
          'hover:text-color-comp-tag-danger-solid-on-container-hover',
          'active:bg-color-comp-tag-danger-solid-container-pressed',
          'active:text-color-comp-tag-danger-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'danger',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-danger-plain-container-hover',
          'hover:text-color-comp-tag-danger-plain-on-container-hover',
          'hover:border-color-comp-tag-danger-plain-border-hover',
          'active:bg-color-comp-tag-danger-plain-container-pressed',
          'active:text-color-comp-tag-danger-plain-on-container-pressed',
          'active:border-color-comp-tag-danger-plain-border-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'warning',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-warning-solid-container-hover',
          'hover:text-color-comp-tag-warning-solid-on-container-hover',
          'active:bg-color-comp-tag-warning-solid-container-pressed',
          'active:text-color-comp-tag-warning-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'warning',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-warning-plain-container-hover',
          'hover:text-color-comp-tag-warning-plain-on-container-hover',
          'hover:border-color-comp-tag-warning-plain-border-hover',
          'active:bg-color-comp-tag-warning-plain-container-pressed',
          'active:text-color-comp-tag-warning-plain-on-container-pressed',
          'active:border-color-comp-tag-warning-plain-border-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'neutral',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-neutral-solid-container-hover',
          'hover:text-color-comp-tag-neutral-solid-on-container-hover',
          'active:bg-color-comp-tag-neutral-solid-container-pressed',
          'active:text-color-comp-tag-neutral-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'neutral',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-neutral-plain-container-hover',
          'hover:text-color-comp-tag-neutral-plain-on-container-hover',
          'hover:border-color-comp-tag-neutral-plain-border-hover',
          'active:bg-color-comp-tag-neutral-plain-container-pressed',
          'active:text-color-comp-tag-neutral-plain-on-container-pressed',
          'active:border-color-comp-tag-neutral-plain-border-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'strong',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-strong-solid-container-hover',
          'hover:text-color-comp-tag-strong-solid-on-container-hover',
          'active:bg-color-comp-tag-strong-solid-container-pressed',
          'active:text-color-comp-tag-strong-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'strong',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-strong-plain-container-hover',
          'hover:text-color-comp-tag-strong-plain-on-container-hover',
          'hover:border-color-comp-tag-strong-plain-border-hover',
          'active:bg-color-comp-tag-strong-plain-container-pressed',
          'active:text-color-comp-tag-strong-plain-on-container-pressed',
          'active:border-color-comp-tag-strong-plain-border-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'transparent',
        fill: 'filled',
        className: [
          'hover:bg-color-comp-tag-transparent-solid-container-hover',
          'hover:text-color-comp-tag-transparent-solid-on-container-hover',
          'active:bg-color-comp-tag-transparent-solid-container-pressed',
          'active:text-color-comp-tag-transparent-solid-on-container-pressed',
        ],
      },
      {
        clickable: true,
        variant: 'transparent',
        fill: 'outline',
        className: [
          'hover:bg-color-comp-tag-transparent-plain-container-hover',
          'hover:text-color-comp-tag-transparent-plain-on-container-hover',
          'hover:border-color-comp-tag-transparent-plain-border-hover',
          'active:bg-color-comp-tag-transparent-plain-container-pressed',
          'active:text-color-comp-tag-transparent-plain-on-container-pressed',
          'active:border-color-comp-tag-transparent-plain-border-pressed',
        ],
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'small',
      fill: 'filled',
    },
  },
);
